/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState, ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";

import map from "lodash/map";
import Pagination from "rc-pagination";

import {
  DetailsList,
  DetailsListLayoutMode,
  Dropdown,
  IColumn,
  IDetailsRowProps,
  PrimaryButton,
  SelectionMode,
  Spinner,
  SpinnerSize,
  Stack,
  StackItem,
  Text,
} from "@fluentui/react";

import ChevronIcon from "Components/Common/ChevronIcon/ChevronIcon";
import { PaginationRow } from "Components/Common/Pagination/Pagination.styles";
import {
  filterButtonStyles,
  highActivitiesEventsDetailsHeaderStyles,
} from "Components/Common/SharedListStyles.styles";
import {
  bodyContainerStackStyles,
  bodyContainerStackTokens,
  headerStackStyles,
  headerTextStyles,
  ContainerWrapper,
  eventsAndActivityBodyContainerStackStyles,
} from "Components/Common/SharedTableStyles.styles";
import {
  defaultActivitiesEventsFilterValues,
  defaultListingPageSize,
  mvpActivitiesEventsFilterFields,
} from "Constants/ApplicationConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { buttonStackTokens } from "Containers/Common/CommonContainer.styles";
import SideBarContainer from "Containers/SideBarContainer/SideBarContainer";
import { getActivityEventTypes } from "Dataflows/ActivityEventTypes/ActivityEventTypesAction";
import {
  addActivitiesEventsFilterValue,
  fetchActivityEventById,
  fetchHighlightedActivitiesEventsBySelectedApplicationId,
  removeActivitiesEventsFilterValue,
  removeMultipleActivitiesEventsFilterValues,
  resetActivitiesEventsFilterValues,
  updateActivitiesEventsCurrentPage,
  updateActivitiesEventsPageSize,
  ActivitiesEventsActionTypes,
} from "Dataflows/ApplicationDetails/ApplicationDetailsAction";
import {
  getHighlightedActivitiesEventsBySelectedApplicationId,
  getHighlightedActivitiesEventsBySelectedApplicationIdErrorMessage,
  getHighlightedActivitiesEventsCurrentPage,
  getHighlightedActivitiesEventsFilteredCount,
  getHighlightedActivitiesEventsPageSize,
  getIsLoadingHighlightedActivitiesEventsBySelectedApplicationId,
  getSelectedHighlightedActivitiesEventsFilterValues,
  getUserProfileInfoBySelectedApplicationId,
  selectTechnologyAreaFilterValuesByAreaGroups,
} from "Dataflows/ApplicationDetails/ApplicationDetailsSelector";
import { getTechnologyAreas } from "Dataflows/TechnologyArea/TechnologyAreaAction";
import { IApplicationFormActivityEvent } from "Interfaces/Application/IApplicationFormActivityEvent";
import { IFilterValue } from "Interfaces/IFilterValue";

import { ActivityEventDetailsSidePanel } from "../ActivityEventDetailsSidePanel";

const HighlightedActivitiesEvents = (): ReactElement => {
  const dispatch = useDispatch();
  const highlightedActivitiesEvents: IApplicationFormActivityEvent[] | undefined = useSelector(
    getHighlightedActivitiesEventsBySelectedApplicationId
  );
  const errorMessage = useSelector(
    getHighlightedActivitiesEventsBySelectedApplicationIdErrorMessage
  );
  const isLoading = useSelector(getIsLoadingHighlightedActivitiesEventsBySelectedApplicationId);
  const selectedFilterValues = useSelector(getSelectedHighlightedActivitiesEventsFilterValues);
  const userProfileInfo = useSelector(getUserProfileInfoBySelectedApplicationId);
  const pageSize: number = useSelector(getHighlightedActivitiesEventsPageSize);
  const currentPage: number = useSelector(getHighlightedActivitiesEventsCurrentPage);
  const totalCount = useSelector(getHighlightedActivitiesEventsFilteredCount);
  const [isActivityEventDetailsPanelOpen, setIsActivityEventDetailsPanelOpen] = useState(false);
  const [techAreaGroupsRemoved, setTechAreaGroupsRemoved] = useState([""]);
  const [lastSelectedSortColum, setLastSelectedSortColumn] = useState("");
  const [isSelectedSortColumDesc, setIsSelectedSortColumnDesc] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);

  const onEventIdSelected = (eventId: number): void => {
    setSelectedEventId(eventId);
    const selectedEvent = highlightedActivitiesEvents?.find(
      (event) => event.activityEventId === eventId
    );
    if (selectedEvent) {
      dispatch(
        fetchActivityEventById(selectedEvent.activityEventId, selectedEvent.contributionType)
      );
    }
  };

  const getPreviousEventId = (): number | null => {
    if (!highlightedActivitiesEvents || selectedEventId === null) return null;
    const currentIndex = highlightedActivitiesEvents.findIndex(
      (event) => event.activityEventId === selectedEventId
    );
    return currentIndex > 0 ? highlightedActivitiesEvents[currentIndex - 1].activityEventId : null;
  };

  const getNextEventId = (): number | null => {
    if (!highlightedActivitiesEvents || selectedEventId === null) return null;
    const currentIndex = highlightedActivitiesEvents.findIndex(
      (event) => event.activityEventId === selectedEventId
    );
    return currentIndex < highlightedActivitiesEvents.length - 1
      ? highlightedActivitiesEvents[currentIndex + 1].activityEventId
      : null;
  };

  const columns: IColumn[] = [
    {
      key: "activityEventName",
      name: "Title",
      ariaLabel: "The Activity Event Title",
      fieldName: "activityEventName",
      minWidth: 100,
      isResizable: true,
      flexGrow: 1,
      isSorted: lastSelectedSortColum == "activityEventName",
      isSortedDescending: isSelectedSortColumDesc && lastSelectedSortColum == "activityEventName",
    },
    {
      key: "contributionType",
      name: "Type",
      ariaLabel: "The type",
      fieldName: "contributionType",
      minWidth: 50,
      isResizable: true,
      flexGrow: 1,
    },
    {
      key: "activityEventType",
      name: "Activity/Event Type",
      ariaLabel: "The activity/event type.",
      fieldName: "activityEventType",
      minWidth: 90,
      isResizable: true,
      flexGrow: 1,
    },
    {
      key: "awardCategory",
      name: "Award Category",
      ariaLabel: "The award category",
      fieldName: "awardCategory",
      minWidth: 90,
      isResizable: true,
      flexGrow: 1,
      isSorted: lastSelectedSortColum == "awardCategory",
      isSortedDescending: isSelectedSortColumDesc && lastSelectedSortColum == "awardCategory",
    },
    {
      key: "technologyArea",
      name: "Primary Technology Area",
      ariaLabel: "The technology area",
      fieldName: "technologyArea",
      minWidth: 120,
      isResizable: true,
      flexGrow: 1,
      isSorted: lastSelectedSortColum == "technologyArea",
      isSortedDescending: isSelectedSortColumDesc && lastSelectedSortColum == "technologyArea",
    },
    {
      key: "quantity",
      name: "Quantity",
      ariaLabel: "The quantity",
      fieldName: "quantity",
      minWidth: 50,
      isResizable: true,
      flexGrow: 1,
      isSorted: lastSelectedSortColum == "quantity",
      isSortedDescending: isSelectedSortColumDesc && lastSelectedSortColum == "quantity",
    },
    {
      key: "liveStreamViews",
      name: "Live Stream Views",
      ariaLabel: "The liveStreamViews",
      fieldName: "liveStreamViews",
      minWidth: 50,
      flexGrow: 1,
      isResizable: true,
      isSorted: lastSelectedSortColum == "liveStreamViews",
      isSortedDescending: isSelectedSortColumDesc && lastSelectedSortColum == "liveStreamViews",
    },
    {
      key: "liveOnlineViews",
      name: "Live Online Views",
      ariaLabel: "The liveOnlineViews",
      fieldName: "liveOnlineViews",
      minWidth: 50,
      flexGrow: 1,
      isResizable: true,
      isSorted: lastSelectedSortColum == "liveOnlineViews",
      isSortedDescending: isSelectedSortColumDesc && lastSelectedSortColum == "liveOnlineViews",
    },
    {
      key: "inPersonAttendees",
      name: "In-Person Attendees",
      ariaLabel: "The inPersonAttendees",
      fieldName: "inPersonAttendees",
      minWidth: 50,
      flexGrow: 1,
      isResizable: true,
      isSorted: lastSelectedSortColum == "inPersonAttendees",
      isSortedDescending: isSelectedSortColumDesc && lastSelectedSortColum == "inPersonAttendees",
    },
    {
      key: "onDemandViews",
      name: "On-Demand Views",
      ariaLabel: "The onDemandViews",
      fieldName: "onDemandViews",
      minWidth: 50,
      flexGrow: 1,
      isResizable: true,
      isSorted: lastSelectedSortColum == "onDemandViews",
      isSortedDescending: isSelectedSortColumDesc && lastSelectedSortColum == "onDemandViews",
    },
    {
      key: "subscriberBase",
      name: "Subscriber Base",
      ariaLabel: "The aubscriberBase",
      fieldName: "subscriberBase",
      minWidth: 50,
      flexGrow: 1,
      isResizable: true,
      isSorted: lastSelectedSortColum == "subscriberBase",
      isSortedDescending: isSelectedSortColumDesc && lastSelectedSortColum == "subscriberBase",
    },
    {
      key: "numberOfSessions",
      name: "Number Of Sessions",
      ariaLabel: "The numberOfSessions",
      fieldName: "numberOfSessions",
      minWidth: 50,
      flexGrow: 1,
      isResizable: true,
      isSorted: lastSelectedSortColum == "numberOfSessions",
      isSortedDescending: isSelectedSortColumDesc && lastSelectedSortColum == "numberOfSessions",
    },
    {
      key: "numberOfViews",
      name: "Number Of Views",
      ariaLabel: "The numberOfViews",
      fieldName: "numberOfViews",
      minWidth: 50,
      flexGrow: 1,
      isResizable: true,
      isSorted: lastSelectedSortColum == "numberOfViews",
      isSortedDescending: isSelectedSortColumDesc && lastSelectedSortColum == "numberOfViews",
    },
  ];
  const addFilterValue = (filter: IFilterValue): ActivitiesEventsActionTypes => {
    setTechAreaGroupsRemoved([""]);
    return dispatch(addActivitiesEventsFilterValue(filter, true));
  };
  const removeFilterValue = (filter: IFilterValue): ActivitiesEventsActionTypes =>
    dispatch(removeActivitiesEventsFilterValue(filter, true));
  const removeMultipleFilterValues = (filters: IFilterValue[]): ActivitiesEventsActionTypes =>
    dispatch(removeMultipleActivitiesEventsFilterValues(filters, true));
  const getSelectedActivityEvent = (
    selectedItem: IApplicationFormActivityEvent
  ): ActivitiesEventsActionTypes =>
    dispatch(fetchActivityEventById(selectedItem.activityEventId, selectedItem.contributionType));
  const onItemClicked = (selectedItem: IApplicationFormActivityEvent): void => {
    setSelectedEventId(selectedItem.activityEventId);
    setIsActivityEventDetailsPanelOpen(true);
    dispatch(fetchActivityEventById(selectedItem.activityEventId, selectedItem.contributionType));
    getSelectedActivityEvent(selectedItem);
  };
  const onTechAreaGroupFilterValuesRemovedOrAdded = (filtersNotSelected: IFilterValue[]): void =>
    setTechAreaGroupsRemoved(map(filtersNotSelected, (f) => f.value));
  const techAreasByAreaGroup = useSelector(() =>
    selectTechnologyAreaFilterValuesByAreaGroups(techAreaGroupsRemoved)
  );
  const techAreasToRemove = techAreasByAreaGroup.filter((fl) =>
    selectedFilterValues.some((sfv) => sfv.value === fl.value)
  );
  const onRenderRow = (props?: IDetailsRowProps, defaultRender?: any): React.ReactElement => {
    const selectedItem = props && (props.item as IApplicationFormActivityEvent);
    return defaultRender({
      ...props,
      item: selectedItem,
      onClick: () => !!selectedItem && onItemClicked(selectedItem),
    });
  };
  const onRenderColumn = (
    item: IApplicationFormActivityEvent,
    index?: number,
    column?: IColumn
  ): React.ReactElement => {
    const fieldContent = item[column?.fieldName as keyof IApplicationFormActivityEvent];
    switch (column?.key) {
      case "additionalTechnologyAreas":
      case "targetAudiences":
        return <span>{(fieldContent as string[]).join(", ")}</span>;
      default:
        return <span>{fieldContent}</span>;
    }
  };
  const handleSearch = (
    selectedFilterValues: IFilterValue[],
    page: number,
    pageSize: number,
    sortFieldName?: string,
    isDescendingSort?: boolean
  ): void => {
    !!userProfileInfo?.applicationFormId &&
      dispatch(
        fetchHighlightedActivitiesEventsBySelectedApplicationId(
          Number(userProfileInfo?.applicationFormId),
          String(page),
          String(pageSize),
          selectedFilterValues,
          TenantTypesEnum.MVP,
          sortFieldName,
          isDescendingSort
        )
      );
  };
  const onSortColumn = (column?: IColumn) => {
    const fieldNamesToSort = [
      "activityEventName",
      "awardCategory",
      "technologyArea",
      "quantity",
      "liveStreamViews",
      "liveOnlineViews",
      "inPersonAttendees",
      "onDemandViews",
      "subscriberBase",
      "numberOfSessions",
      "numberOfViews",
    ];
    if (column && column.fieldName && fieldNamesToSort.includes(column.fieldName)) {
      const fieldName = column.fieldName;
      const isDesc = column.isSortedDescending ?? false;
      setLastSelectedSortColumn(fieldName);
      setIsSelectedSortColumnDesc(!isDesc);
      handleSearch(selectedFilterValues, currentPage, pageSize, fieldName, isDesc);
    }
  };

  /**
   * Call to reset filter values.
   * @method
   */
  const resetFilterValues = (): void => {
    dispatch(resetActivitiesEventsFilterValues(true));

    handleSearch(defaultActivitiesEventsFilterValues, 1, defaultListingPageSize);
  };

  useEffect(() => {
    dispatch(getTechnologyAreas(TenantTypesEnum.MVP));
    dispatch(getActivityEventTypes(TenantTypesEnum.MVP));
  }, []);

  useEffect(() => {
    if (techAreasToRemove.length > 0) {
      removeMultipleFilterValues(techAreasToRemove);
    }
  }, [techAreasToRemove]);

  useEffect(() => {
    handleSearch(selectedFilterValues, currentPage, pageSize);
  }, [currentPage, pageSize]);

  return !!isLoading ? (
    <Spinner size={SpinnerSize.large} />
  ) : !!errorMessage ? (
    <p>{errorMessage}</p>
  ) : (
    <Stack>
      <ContainerWrapper>
        <Stack styles={headerStackStyles}>
          <Text styles={headerTextStyles}>Highlighted Activities/Events</Text>
        </Stack>
        <Stack styles={eventsAndActivityBodyContainerStackStyles} tokens={bodyContainerStackTokens}>
          <Stack horizontal horizontalAlign="space-between" tokens={{}}>
            <StackItem styles={{ root: { flexGrow: "1" } }}>
              <>
                <SideBarContainer
                  addFilterValue={addFilterValue}
                  removeFilterValue={removeFilterValue}
                  updateFilterValue={() => {
                    return;
                  }} // this prop is required, but we don't need it for this instance.
                  onTechAreaGroupFilterValueChanged={onTechAreaGroupFilterValuesRemovedOrAdded}
                  selectedFilterValues={selectedFilterValues}
                  filterFields={mvpActivitiesEventsFilterFields}
                />
                <Stack horizontal tokens={buttonStackTokens}>
                  <PrimaryButton
                    styles={filterButtonStyles}
                    text="Apply"
                    onClick={(): void => handleSearch(selectedFilterValues, 1, pageSize)} // Reset to page 1, since we are applying new filters
                  />
                  <PrimaryButton
                    styles={filterButtonStyles}
                    text="Reset"
                    onClick={(): void => resetFilterValues()}
                  />
                </Stack>
              </>
            </StackItem>
            <StackItem styles={{ root: { flexGrow: "3" } }}>
              <DetailsList
                items={highlightedActivitiesEvents || []}
                columns={columns}
                selectionMode={SelectionMode.none}
                getKey={(item: any): string =>
                  `${(item as IApplicationFormActivityEvent).activityEventId}`
                }
                setKey="none"
                layoutMode={DetailsListLayoutMode.justified}
                isHeaderVisible={true}
                onRenderRow={onRenderRow}
                onRenderItemColumn={onRenderColumn}
                enterModalSelectionOnTouch={true}
                styles={highActivitiesEventsDetailsHeaderStyles}
                onColumnHeaderClick={(e, column) => {
                  onSortColumn(column);
                }}
              />
              <PaginationRow>
                <Pagination
                  total={totalCount}
                  onChange={(page): void => {
                    dispatch(updateActivitiesEventsCurrentPage(page, true));
                  }}
                  showTitle={false}
                  current={currentPage}
                  prevIcon={ChevronIcon("ChevronLeft")}
                  nextIcon={ChevronIcon("ChevronRight")}
                  pageSize={pageSize}
                  showSizeChanger
                />
                <Dropdown
                  options={[
                    { key: 16, text: "Show 16 results" },
                    { key: 32, text: "Show 32 results" },
                    { key: 48, text: "Show 48 results" },
                  ]}
                  onChange={(_e, op): void => {
                    dispatch(updateActivitiesEventsPageSize(op?.key as number, true));
                  }}
                  selectedKey={pageSize}
                  className="pagination-dropdown"
                  title="Page Size Dropdown"
                />
              </PaginationRow>
            </StackItem>
          </Stack>
        </Stack>
      </ContainerWrapper>
      {userProfileInfo && (
        <ActivityEventDetailsSidePanel
          isPanelOpen={isActivityEventDetailsPanelOpen}
          setIsPanelOpen={setIsActivityEventDetailsPanelOpen}
          previousEventId={getPreviousEventId()} // Pass the previous event ID
          nextEventId={getNextEventId()} // Pass the next event ID
          onEventIdSelected={onEventIdSelected}
        />
      )}
    </Stack>
  );
};

export default HighlightedActivitiesEvents;
